import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Calendar as CalIcon, Search, Heart, Hand } from "lucide-react";
import OrganizerBadge from "@/components/OrganizerBadge";
import NonprofitBadge from "@/components/NonprofitBadge";
import FollowButton from "@/components/FollowButton";
import { AiFreeBadge } from "@/components/AiFreeToggle";

const CATEGORIES = ["all", "food", "art", "craft", "vintage", "plants", "music", "wellness", "other"];

function EventCard({ e }) {
  return (
    <div
      data-testid={`event-card-${e.id}`}
      className="card-warm rounded-sm overflow-hidden block relative"
    >
      {/* Follow star — floated top-right so vendors can favorite the
          organizer without opening the event. stopPropagation keeps taps
          from bubbling into the wrapping Link below. */}
      {e.organizer_id && (
        <div
          className="absolute top-3 right-3 z-10"
          onClick={(ev) => ev.stopPropagation()}
          onMouseDown={(ev) => ev.stopPropagation()}
          data-testid={`event-card-follow-wrap-${e.id}`}
        >
          <FollowButton
            organizerId={e.organizer_id}
            organizerName={e.organizer_name || ""}
            size="sm"
            testId={`event-card-follow-${e.id}`}
            className="shadow-md"
          />
        </div>
      )}
      <Link to={`/events/${e.id}`} className="block">
        <div className="aspect-[4/3] overflow-hidden bg-[color:var(--sand)]">
          {e.cover_image ? (
            <img src={e.cover_image} alt={e.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-serif text-3xl" style={{ color: "var(--terracotta)" }}>{e.title.slice(0, 1)}</div>
          )}
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between gap-2">
            <div className="overline" style={{ color: "var(--terracotta)" }}>{e.category}</div>
            <div className="inline-flex items-center gap-1.5 flex-wrap">
              {e.is_fundraiser && (
                <span
                  data-testid={`event-fundraiser-badge-${e.id}`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: "rgba(236,72,153,0.14)",
                    color: "var(--avt-pink)",
                    border: "1px solid var(--avt-pink)",
                  }}
                >
                  <Heart className="h-3 w-3" fill="var(--avt-pink)" strokeWidth={0} /> Fundraiser
                </span>
              )}
              <NonprofitBadge user={{ nonprofit_status: e.organizer_is_nonprofit ? "verified" : null }} size="sm" />
              <OrganizerBadge
                verified={!!e.organizer_verified}
                kycApproved={!!e.organizer_kyc_approved}
                compact
              />
              <AiFreeBadge ai_free={e.organizer_ai_free} size="xs" />
            </div>
          </div>
          <h3 className="font-serif text-2xl mt-2 leading-tight">{e.title}</h3>
          <div className="mt-3 flex items-center gap-4 text-xs" style={{ color: "var(--ink-soft)" }}>
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {e.city}</span>
            <span className="flex items-center gap-1"><CalIcon className="h-3.5 w-3.5" /> {e.date}</span>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm">{e.booth_fee > 0 ? `$${e.booth_fee.toFixed(2)} booth` : "Free booth"}</span>
            <span className="overline" style={{ color: "var(--moss)" }}>Apply →</span>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  // Handmade · No AI discovery filter — flip to only see events by
  // organizers who've self-declared no AI in their work.
  const [aiFree, setAiFree] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (city) params.set("city", city);
      if (category && category !== "all") params.set("category", category);
      if (q) params.set("q", q);
      if (aiFree) params.set("ai_free", "true");
      const { data } = await api.get(`/events?${params.toString()}`);
      setEvents(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [aiFree]);

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
      <div className="overline" style={{ color: "var(--terracotta)" }}>Discovery</div>
      <h1 className="font-serif text-5xl mt-3">Local events, open for applications.</h1>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-10 grid md:grid-cols-4 gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
          <Input data-testid="filter-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search events…" className="pl-9" />
        </div>
        <Input data-testid="filter-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c === "all" ? "All categories" : c}</SelectItem>)}
          </SelectContent>
        </Select>
        <button type="submit" data-testid="filter-apply" className="btn-primary rounded-md">Filter</button>
      </form>

      <div className="mt-4">
        <button
          type="button"
          onClick={() => setAiFree((v) => !v)}
          data-testid="filter-ai-free"
          aria-pressed={aiFree}
          className="inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full transition"
          style={{
            background: aiFree ? "var(--moss)" : "rgba(111,125,85,0.14)",
            color: aiFree ? "#fff" : "var(--moss)",
          }}
        >
          <Hand size={11} /> Handmade · No AI only
        </button>
      </div>

      <div className="mt-12">
        {loading ? (
          <div className="text-center py-20 font-serif text-2xl">Loading…</div>
        ) : events.length === 0 ? (
          <div className="text-center py-20">
            <div className="font-serif text-3xl">No events yet.</div>
            <div className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>Check back soon or adjust your filters.</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
            {events.map((e) => <EventCard key={e.id} e={e} />)}
          </div>
        )}
      </div>
    </div>
  );
}
